<template>
	<section class="profile-overview-container">
		<div class="avatar">
			<div class="avatar-icon">
				<span class="initials">{{
					getInitials(leadDetails.firstName, leadDetails.lastName)
				}}</span>
			</div>
		</div>
		<div class="profile-overview">
			<div class="name-container b360-handle-overflow">
				<span
					:title="
						truncateModalFields({
							fieldName: `${leadDetails.firstName} ${leadDetails.lastName}`,
							fieldLength: 28,
							attributeName: 'title',
						})
					"
					class="full-name"
				>
					{{
						truncateModalFields({
							fieldName: `${leadDetails.firstName} ${leadDetails.lastName}`,
							fieldLength: 28,
						})
					}}
				</span>
			</div>
			<div class="contact-type-container">
				<font-awesome-icon class="contact-type-icon" :icon="['far', 'tag']" />
				<span class="type">
					{{ leadDetails.type.desc }}
				</span>
			</div>
			<div class="email-container b360-handle-overflow">
				<font-awesome-icon class="email-icon" :icon="['far', 'envelope']" />
				<a
					class="email"
					:title="
						truncateModalFields({
							fieldName: leadDetails.email,
							attributeLength: 28,
							attributeName: 'title',
						})
					"
					:class="{ disabled: mainProfileIsInternalUser }"
					:href="`mailto:${leadDetails.email}`"
				>
					{{ truncateModalFields({ fieldName: leadDetails.email }) }}
				</a>
			</div>
			<div v-if="leadDetails.phones.length > 0" class="phone-container">
				<font-awesome-icon class="phone-icon" :icon="['far', 'phone-alt']" />
				<a
					class="phone"
					data-test="phone-number"
					:class="{ disabled: mainProfileIsInternalUser }"
					:href="`tel:${formattedPhoneNumber}`"
					>{{ formattedPhoneNumber }}</a
				>
			</div>
			<div v-if="address" class="address-container">
				<font-awesome-icon class="home-icon" :icon="['far', 'home-lg-alt']" />
				<div class="address b360-handle-overflow">
					<span
						class="street"
						:title="
							truncateModalFields({
								fieldName: address,
								attributeName: 'title',
							})
						"
					>
						{{ truncateModalFields({ fieldName: address }) }}
					</span>
					<span
						class="city-state-zipcode"
						:title="
							truncateModalFields({
								fieldName: cityStateAndZipCode,
								fieldLength: 30,
								attributeName: 'title',
							})
						"
					>
						{{
							truncateModalFields({
								fieldName: cityStateAndZipCode,
								fieldLength: 30,
							})
						}}
					</span>
				</div>
			</div>
			<div v-if="formattedBirthday" class="birth-date-container">
				<font-awesome-icon class="cake-icon" :icon="['far', 'birthday-cake']" />
				<span class="birth-date">
					{{ formattedBirthday }}
				</span>
			</div>
		</div>
	</section>
</template>

<script>
import { getInitials, truncateModalFields } from '../helpers';

export default {
	name: 'ProfileOverview',
	props: {
		leadDetails: Object,
		mainProfileIsInternalUser: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		formattedPhoneNumber() {
			const storedPhoneNumber = this.leadDetails.phones[0].phoneNumber;
			if (storedPhoneNumber) {
				const phoneNumber = storedPhoneNumber.replace(/\D/g, '');
				return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
					3,
					6,
				)}-${phoneNumber.slice(6, 10)}`;
			} else {
				return null;
			}
		},
		formattedBirthday() {
			const { birthday } = this.leadDetails;
			return birthday && new Date(birthday).getFullYear() !== 1
				? new Date(birthday).toLocaleDateString('en-US', {
						month: 'long',
						day: 'numeric',
				  })
				: null;
		},
		address() {
			const { primaryAddress } = this.leadDetails;
			return primaryAddress.addressLine1
				? `${primaryAddress.addressLine1} ${
						primaryAddress.addressLine2 ? primaryAddress.addressLine2 : ''
				  }`
				: null;
		},
		cityStateAndZipCode() {
			const { primaryAddress } = this.leadDetails;
			return primaryAddress.city
				? `${primaryAddress.city} ${primaryAddress.state} ${primaryAddress.zipCode}`
				: null;
		},
	},
	methods: {
		getInitials,
		truncateModalFields,
	},
};
</script>

<style lang="scss" scoped>
.profile-overview-container {
	display: grid;
	grid-template-columns: 100px auto;
	@include rem('gap', 20px);
	@media only screen and (max-width: 600px) {
		grid-template-columns: auto;
	}

	.profile-overview {
		display: grid;
		@include rem('gap', 10px);

		.full-name {
			font-weight: 500;
			@include rem('font-size', 18px);
			@include rem('margin-right', 10px);
		}

		.email-container,
		.phone-container,
		.address-container,
		.birth-date-container,
		.contact-type-container {
			display: grid;
			grid-template-columns: 16px auto;
			@include rem('gap', 10px);
		}

		.contact-type-icon,
		.email-icon,
		.phone-icon,
		.home-icon,
		.cake-icon {
			color: $black;
			justify-self: center;
		}

		.type,
		.address,
		.birth-date {
			color: $b360-color--gray__darker;
		}

		.email,
		.phone {
			color: $brand-blue;
			@include typo--roboto__medium;
			text-decoration: none;
			line-height: 1;
			&.disabled {
				font-weight: 400;
				color: $b360-color--gray__darker;
				pointer-events: none;
			}
		}

		.address {
			display: grid;
			@include rem('gap', 5px);
		}
	}

	.avatar-icon {
		background-color: $brand-blue;
		text-align: center;
		border-radius: 8px;
		display: flex;
		align-items: center;
		justify-content: center;
		@include rem('width', 100px);
		@include rem('height', 100px);

		.initials {
			color: $white;
			@include rem('font-size', 50px);
		}
	}
}
</style>
